import { useState, useEffect, useCallback } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer,
  Legend, LabelList,
} from "recharts";
import {
  fetchScoreHealth, fetchScoreAgreement, fetchCnaAgreement, fetchSpendHistory,
  fetchProductIngestion,
} from "../api";
import { fmtTs } from "../utils/time";
import { VINNIE_LLM_MODEL } from "../utils/cvss";

const DAY_OPTIONS = [1, 3, 5, 7, 14, 30];
const TABS = ["coverage", "agreement", "spend", "products"];

function StatTile({ label, value, total, color, suffix }) {
  return (
    <div className="panel" style={{ padding: "12px 18px", minWidth: 140, textAlign: "center" }}>
      <div style={{ fontSize: 26, fontWeight: 700, color: color || "var(--text-primary)" }}>
        {value}{suffix && <span style={{ fontSize: 14, fontWeight: 400 }}>{suffix}</span>}
      </div>
      <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>
        {label}{total != null && <span style={{ color: "var(--text-muted)" }}> / {total}</span>}
      </div>
    </div>
  );
}

function MissingBadge({ ok, label }) {
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 3,
      textTransform: "uppercase", letterSpacing: "0.3px", marginRight: 4,
      background: ok ? "rgba(63,185,80,0.15)" : "rgba(248,81,73,0.15)",
      color: ok ? "#3fb950" : "#f85149",
    }}>
      {label}
    </span>
  );
}

function pctColor(pct) {
  if (pct >= 80) return "#3fb950";
  if (pct >= 60) return "#d29922";
  return "#f85149";
}

function deltaColor(d) {
  if (d <= 0.5) return "#3fb950";
  if (d <= 1.5) return "#d29922";
  return "#f85149";
}

function CoverageTab({ days, data, loading }) {
  const s = data?.summary;
  return (
    <>
      {s && (
        <div style={{ display: "flex", gap: 12, marginBottom: 12, flexWrap: "wrap", opacity: loading ? 0.6 : 1 }}>
          <StatTile label="CVEs in window" value={s.total} />
          <StatTile label="No scores at all" value={s.no_scores} total={s.total}
                    color={s.no_scores > 0 ? "#f85149" : "#3fb950"} />
          <StatTile label="Missing LLM scores" value={s.no_llm} total={s.total}
                    color={s.no_llm > 0 ? "#d29922" : "#3fb950"} />
          <StatTile label="Missing Vinnie (gemma)" value={s.no_vinnie} total={s.total}
                    color={s.no_vinnie > 0 ? "#d29922" : "#3fb950"} />
        </div>
      )}
      {data && data.cves.length === 0 && (
        <div className="panel" style={{ padding: 20, textAlign: "center", color: "#3fb950", fontWeight: 600 }}>
          All {s.total} CVEs in the window are fully scored.
        </div>
      )}
      {data && data.cves.length > 0 && (
        <div className="panel" style={{ opacity: loading ? 0.6 : 1 }}>
          <div className="panel-header">
            Missing scores ({s.missing_shown}{s.missing_total > s.missing_shown ? ` of ${s.missing_total}` : ""})
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "2px solid var(--border)" }}>
                  <th style={{ textAlign: "left", padding: 8 }}>CVE</th>
                  <th style={{ textAlign: "left", padding: 8 }}>Published</th>
                  <th style={{ textAlign: "center", padding: 8 }}>Refs</th>
                  <th style={{ textAlign: "center", padding: 8 }}>Refs fetched</th>
                  <th style={{ textAlign: "left", padding: 8 }}>Scores</th>
                </tr>
              </thead>
              <tbody>
                {data.cves.map((c) => (
                  <tr key={c.cve} style={{ borderBottom: "1px solid var(--border)" }}>
                    <td style={{ padding: 8 }}>
                      <a href={`/cve/${c.cve}`} style={{ color: "var(--accent)", fontWeight: 600 }}>
                        {c.cve}
                      </a>
                    </td>
                    <td style={{ padding: 8, color: "var(--text-secondary)" }}>
                      {fmtTs(c.published_at || c.imported_at)}
                    </td>
                    <td style={{ padding: 8, textAlign: "center" }}>{c.refs}</td>
                    <td style={{ padding: 8, textAlign: "center" }}>
                      {c.refs_fetched ? "✓" : <span style={{ color: "#f85149" }}>{"✗"}</span>}
                    </td>
                    <td style={{ padding: 8 }}>
                      <MissingBadge ok={c.has_any_scores} label="any" />
                      <MissingBadge ok={c.has_llm} label="llm" />
                      <MissingBadge ok={c.has_vinnie} label="gemma" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

function DeltaBar({ buckets, total }) {
  if (!total) return null;
  const order = ["0", "0.1-1.0", "1.1-2.0", "2.1-3.0", "3.0+"];
  const colors = ["#3fb950", "#58a6ff", "#d29922", "#f0883e", "#f85149"];
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", height: 24, borderRadius: 4, overflow: "hidden", marginBottom: 6 }}>
        {order.map((k, i) => {
          const pct = (buckets[k] || 0) / total * 100;
          if (pct === 0) return null;
          return (
            <div key={k} title={`${k}: ${buckets[k]} (${pct.toFixed(1)}%)`}
                 style={{ width: `${pct}%`, background: colors[i], minWidth: pct > 0 ? 2 : 0,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 10, fontWeight: 700, color: "#fff" }}>
              {pct >= 8 ? `${buckets[k]}` : ""}
            </div>
          );
        })}
      </div>
      <div style={{ display: "flex", gap: 14, fontSize: 11, color: "var(--text-secondary)", flexWrap: "wrap" }}>
        {order.map((k, i) => (
          <span key={k}>
            <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: colors[i], marginRight: 4, verticalAlign: "middle" }} />
            {k === "0" ? "Exact" : k}
            <span style={{ color: "var(--text-muted)", marginLeft: 3 }}>
              {buckets[k] || 0}
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}

const CNA_COLUMNS = [
  { key: "cna", label: "CNA", align: "left" },
  { key: "cves", label: "CVEs", align: "right" },
  { key: "compared", label: "Compared", align: "right" },
  { key: "severity_match_pct", label: "Severity match", align: "right" },
  { key: "within_1_nvd", label: "±1.0 NVD", align: "right" },
  { key: "within_1_cna", label: "±1.0 CNA", align: "right" },
  { key: "avg_delta_nvd", label: "Δ vs NVD", align: "right" },
  { key: "stdev_nvd", label: "σ NVD", align: "right" },
  { key: "avg_delta_cna", label: "Δ vs CNA", align: "right" },
  { key: "stdev_cna", label: "σ CNA", align: "right" },
];

function sortVal(row, key) {
  const v = row[key];
  return v == null ? -Infinity : v;
}

function SortTh({ col, sortKey, sortDir, onSort }) {
  const active = sortKey === col.key;
  return (
    <th style={{ textAlign: col.align, padding: 8, cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}
        onClick={() => onSort(col.key)}>
      {col.label}
      <span style={{ marginLeft: 4, fontSize: 10, opacity: active ? 1 : 0.3 }}>
        {active ? (sortDir === "asc" ? "▲" : "▼") : "▼"}
      </span>
    </th>
  );
}

function CnaModal({ cna, days, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchCnaAgreement(cna, days)
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [cna, days]);

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const compared = data?.cves?.filter(c => c.compared) || [];
  const sevMatch = data?.severity_match || 0;

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center",
    }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: "var(--bg-primary)", borderRadius: 8, border: "1px solid var(--border)",
        width: "min(900px, 92vw)", maxHeight: "88vh", overflow: "hidden",
        display: "flex", flexDirection: "column",
      }}>
        <div style={{
          padding: "14px 20px", borderBottom: "1px solid var(--border)",
          display: "flex", alignItems: "center", gap: 12,
        }}>
          <span style={{ fontWeight: 700, fontSize: 16 }}>{cna}</span>
          {data && (
            <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>
              {data.total} CVEs, {data.compared} compared, {sevMatch} severity match
              ({data.compared ? Math.round(100 * sevMatch / data.compared) : 0}%)
            </span>
          )}
          <button onClick={onClose} style={{
            marginLeft: "auto", background: "none", border: "none", cursor: "pointer",
            fontSize: 18, color: "var(--text-secondary)", padding: "0 4px",
          }}>{"✕"}</button>
        </div>

        <div style={{ overflow: "auto", padding: 16 }}>
          {loading && <div style={{ textAlign: "center", padding: 20, color: "var(--text-secondary)" }}>Loading...</div>}
          {data && !loading && (
            <>
              {data.components && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Component agreement</div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
                    {data.components.map(c => (
                      <div key={c.metric} style={{
                        padding: "6px 12px", borderRadius: 4, fontSize: 12, textAlign: "center",
                        border: "1px solid var(--border)", minWidth: 70,
                      }}>
                        <div style={{ fontWeight: 700, fontSize: 16, color: pctColor(c.pct) }}>{c.pct}%</div>
                        <div style={{ color: "var(--text-secondary)" }}>{c.metric}</div>
                        {c.diffs && c.diffs.length > 0 && (
                          <div style={{ marginTop: 4, fontSize: 10, color: "var(--text-muted)" }}>
                            {c.diffs.slice(0, 3).map(d => (
                              <div key={d.change}>{d.change} ({d.count})</div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>
                CVEs ({compared.length} compared, sorted by delta)
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ borderBottom: "2px solid var(--border)" }}>
                      <th style={{ textAlign: "left", padding: "6px 8px" }}>CVE</th>
                      <th style={{ textAlign: "right", padding: "6px 8px" }}>Official</th>
                      <th style={{ textAlign: "right", padding: "6px 8px" }}>LLM</th>
                      <th style={{ textAlign: "right", padding: "6px 8px" }}>Delta</th>
                      <th style={{ textAlign: "left", padding: "6px 8px" }}>Differences</th>
                    </tr>
                  </thead>
                  <tbody>
                    {compared.map(c => (
                      <tr key={c.cve} style={{ borderBottom: "1px solid var(--border)" }}>
                        <td style={{ padding: "6px 8px" }}>
                          <a href={`/cve/${c.cve}`} style={{ color: "var(--accent)", fontWeight: 600 }}>
                            {c.cve}
                          </a>
                        </td>
                        <td style={{ padding: "6px 8px", textAlign: "right" }}>
                          <span style={{ color: c.sev_match ? "var(--text-primary)" : "#f85149" }}>
                            {c.official_score} <span style={{ fontSize: 10, color: "var(--text-secondary)" }}>({c.official_sev})</span>
                          </span>
                        </td>
                        <td style={{ padding: "6px 8px", textAlign: "right" }}>
                          {c.llm_score} <span style={{ fontSize: 10, color: "var(--text-secondary)" }}>({c.llm_sev})</span>
                        </td>
                        <td style={{ padding: "6px 8px", textAlign: "right", fontWeight: 700, color: deltaColor(c.delta) }}>
                          {c.delta}
                        </td>
                        <td style={{ padding: "6px 8px", fontSize: 11, color: "var(--text-secondary)" }}>
                          {c.diffs && c.diffs.length > 0
                            ? c.diffs.join(", ")
                            : <span style={{ color: "#3fb950" }}>exact</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {data.cves.filter(c => !c.compared).length > 0 && (
                <div style={{ marginTop: 8, fontSize: 11, color: "var(--text-muted)" }}>
                  {data.cves.filter(c => !c.compared).length} CVEs without official CVSS (not compared)
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function AgreementTab({ data, loading, days }) {
  const [showDisagreements, setShowDisagreements] = useState(false);
  const [sortKey, setSortKey] = useState("cves");
  const [sortDir, setSortDir] = useState("desc");
  const [modalCna, setModalCna] = useState(null);

  if (!data) return null;
  const { summary, model_table, cna_table, disagreements, total_compared, total_cves, by_source } = data;

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === "desc" ? "asc" : "desc");
    } else {
      setSortKey(key);
      setSortDir(key === "cna" ? "asc" : "desc");
    }
  };

  const sorted = [...cna_table].sort((a, b) => {
    const av = sortVal(a, sortKey);
    const bv = sortVal(b, sortKey);
    const cmp = sortKey === "cna"
      ? String(av).localeCompare(String(bv))
      : (av < bv ? -1 : av > bv ? 1 : 0);
    return sortDir === "asc" ? cmp : -cmp;
  });

  return (
    <div style={{ opacity: loading ? 0.6 : 1 }}>
      <div style={{ display: "flex", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
        <StatTile label="CVEs in window" value={total_cves} />
        <StatTile label="Compared (have official)" value={total_compared} total={total_cves} />
        <StatTile label="Severity match" value={summary.severity_match_pct} suffix="%"
                  color={pctColor(summary.severity_match_pct)} />
        <StatTile label="Within 1.0 pt" value={summary.within_1_pct} suffix="%"
                  color={pctColor(summary.within_1_pct)} />
        <StatTile label="Exact vector" value={summary.exact_vector_pct} suffix="%"
                  color={pctColor(summary.exact_vector_pct)} />
      </div>

      {by_source && by_source.length > 0 && (
        <div className="panel" style={{ marginBottom: 12 }}>
          <div className="panel-header">Agreement by official source</div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "2px solid var(--border)" }}>
                  <th style={{ textAlign: "left", padding: 8 }}>Source</th>
                  <th style={{ textAlign: "right", padding: 8 }}>Compared</th>
                  <th style={{ textAlign: "right", padding: 8 }}>Severity match</th>
                  <th style={{ textAlign: "right", padding: 8 }}>Within 1.0</th>
                  <th style={{ textAlign: "right", padding: 8 }}>Exact vector</th>
                  <th style={{ textAlign: "right", padding: 8 }}>Avg delta</th>
                </tr>
              </thead>
              <tbody>
                {by_source.map(row => (
                  <tr key={row.source} style={{ borderBottom: "1px solid var(--border)" }}>
                    <td style={{ padding: 8, fontWeight: 600 }}>{row.source}</td>
                    <td style={{ padding: 8, textAlign: "right" }}>{row.compared}</td>
                    <td style={{ padding: 8, textAlign: "right" }}>
                      <span style={{ color: pctColor(row.severity_match_pct) }}>{row.severity_match_pct}%</span>
                    </td>
                    <td style={{ padding: 8, textAlign: "right" }}>
                      <span style={{ color: pctColor(row.within_1_pct) }}>{row.within_1_pct}%</span>
                    </td>
                    <td style={{ padding: 8, textAlign: "right" }}>
                      <span style={{ color: pctColor(row.exact_vector_pct) }}>{row.exact_vector_pct}%</span>
                    </td>
                    <td style={{ padding: 8, textAlign: "right" }}>
                      <span style={{ color: deltaColor(row.avg_delta) }}>{row.avg_delta}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="panel" style={{ marginBottom: 12 }}>
        <div className="panel-header">Score delta distribution (NVD/Vendor vs Vinnie (gemma))</div>
        <div style={{ padding: "12px 16px" }}>
          <DeltaBar buckets={summary.delta_buckets} total={total_compared} />
        </div>
      </div>

      {model_table && model_table.length > 0 && (
        <div className="panel" style={{ marginBottom: 12 }}>
          <div className="panel-header">Agreement by model</div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "2px solid var(--border)" }}>
                  <th style={{ textAlign: "left", padding: 8 }}>Model</th>
                  <th style={{ textAlign: "right", padding: 8 }}>Compared</th>
                  <th style={{ textAlign: "right", padding: 8 }}>Severity match</th>
                  <th style={{ textAlign: "right", padding: 8 }}>Within 1.0</th>
                  <th style={{ textAlign: "right", padding: 8 }}>Exact vector</th>
                  <th style={{ textAlign: "right", padding: 8 }}>Avg delta</th>
                </tr>
              </thead>
              <tbody>
                {model_table.map((row) => (
                  <tr key={row.model} style={{
                    borderBottom: "1px solid var(--border)",
                    background: row.model?.toLowerCase() === VINNIE_LLM_MODEL ? "rgba(88,166,255,0.06)" : undefined,
                  }}>
                    <td style={{ padding: 8, fontWeight: row.model?.toLowerCase() === VINNIE_LLM_MODEL ? 700 : 600 }}>{row.model}</td>
                    <td style={{ padding: 8, textAlign: "right" }}>{row.compared}</td>
                    <td style={{ padding: 8, textAlign: "right" }}>
                      <span style={{ color: pctColor(row.severity_match_pct) }}>{row.severity_match_pct}%</span>
                    </td>
                    <td style={{ padding: 8, textAlign: "right" }}>
                      <span style={{ color: pctColor(row.within_1_pct) }}>{row.within_1_pct}%</span>
                    </td>
                    <td style={{ padding: 8, textAlign: "right" }}>
                      <span style={{ color: pctColor(row.exact_vector_pct) }}>{row.exact_vector_pct}%</span>
                    </td>
                    <td style={{ padding: 8, textAlign: "right" }}>
                      <span style={{ color: deltaColor(row.avg_delta) }}>{row.avg_delta}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="panel" style={{ marginBottom: 12 }}>
        <div className="panel-header">CVEs by CNA</div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "2px solid var(--border)" }}>
                {CNA_COLUMNS.map(col => (
                  <SortTh key={col.key} col={col} sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((row) => (
                <tr key={row.cna} style={{ borderBottom: "1px solid var(--border)" }}>
                  <td style={{ padding: 8, fontWeight: 600 }}>
                    <span style={{ cursor: "pointer", color: "var(--accent)" }}
                          onClick={() => setModalCna(row.cna)}>
                      {row.cna}
                    </span>
                  </td>
                  <td style={{ padding: 8, textAlign: "right" }}>{row.cves}</td>
                  <td style={{ padding: 8, textAlign: "right", color: "var(--text-secondary)" }}>
                    {row.compared ?? "—"}
                  </td>
                  <td style={{ padding: 8, textAlign: "right" }}>
                    {row.compared ? (
                      <span style={{ color: pctColor(row.severity_match_pct) }}>
                        {row.severity_match_pct}%
                      </span>
                    ) : "—"}
                  </td>
                  <td style={{ padding: 8, textAlign: "right" }}>
                    {row.within_1_nvd != null ? (
                      <span style={{ color: pctColor(row.within_1_nvd) }}>
                        {row.within_1_nvd}%
                      </span>
                    ) : "—"}
                  </td>
                  <td style={{ padding: 8, textAlign: "right" }}>
                    {row.within_1_cna != null ? (
                      <span style={{ color: pctColor(row.within_1_cna) }}>
                        {row.within_1_cna}%
                      </span>
                    ) : "—"}
                  </td>
                  <td style={{ padding: 8, textAlign: "right" }}>
                    {row.avg_delta_nvd != null ? (
                      <span style={{ color: deltaColor(row.avg_delta_nvd) }}>
                        {row.avg_delta_nvd}
                      </span>
                    ) : "—"}
                  </td>
                  <td style={{ padding: 8, textAlign: "right", color: "var(--text-secondary)" }}>
                    {row.stdev_nvd != null ? row.stdev_nvd : "—"}
                  </td>
                  <td style={{ padding: 8, textAlign: "right" }}>
                    {row.avg_delta_cna != null ? (
                      <span style={{ color: deltaColor(row.avg_delta_cna) }}>
                        {row.avg_delta_cna}
                      </span>
                    ) : "—"}
                  </td>
                  <td style={{ padding: 8, textAlign: "right", color: "var(--text-secondary)" }}>
                    {row.stdev_cna != null ? row.stdev_cna : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {disagreements.length > 0 && (
        <div className="panel">
          <div className="panel-header" style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
               onClick={() => setShowDisagreements(!showDisagreements)}>
            <span>{showDisagreements ? "▼" : "▶"} Large disagreements ({disagreements.length})</span>
            <span style={{ marginLeft: 8, fontSize: 11, color: "var(--text-secondary)", fontWeight: 400 }}>
              delta &ge; 2.0
            </span>
          </div>
          {showDisagreements && (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid var(--border)" }}>
                    <th style={{ textAlign: "left", padding: 8 }}>CVE</th>
                    <th style={{ textAlign: "left", padding: 8 }}>CNA</th>
                    <th style={{ textAlign: "right", padding: 8 }}>Official</th>
                    <th style={{ textAlign: "right", padding: 8 }}>LLM</th>
                    <th style={{ textAlign: "right", padding: 8 }}>Delta</th>
                  </tr>
                </thead>
                <tbody>
                  {disagreements.map((d) => (
                    <tr key={d.cve} style={{ borderBottom: "1px solid var(--border)" }}>
                      <td style={{ padding: 8 }}>
                        <a href={`/cve/${d.cve}`} style={{ color: "var(--accent)", fontWeight: 600 }}>
                          {d.cve}
                        </a>
                      </td>
                      <td style={{ padding: 8, color: "var(--text-secondary)" }}>{d.cna}</td>
                      <td style={{ padding: 8, textAlign: "right" }}>{d.official}</td>
                      <td style={{ padding: 8, textAlign: "right" }}>{d.llm}</td>
                      <td style={{ padding: 8, textAlign: "right", color: "#f85149", fontWeight: 700 }}>
                        {d.delta}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {modalCna && <CnaModal cna={modalCna} days={days} onClose={() => setModalCna(null)} />}
    </div>
  );
}

function fmtUsd(v, digits = 2) {
  if (v == null) return "—";
  return `$${v.toFixed(digits)}`;
}

function costPerCve(total, cves) {
  if (total == null || !cves) return null;
  return total / cves;
}

function SpendTab({ data, loading }) {
  if (!data) return null;
  const rows = data.rows || [];
  const totals = data.totals || {};
  const overallCost = costPerCve(totals.combined, totals.cves_analyzed);
  const cellR = { padding: 8, textAlign: "right" };

  return (
    <div style={{ opacity: loading ? 0.6 : 1 }}>
      <div style={{ display: "flex", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
        <StatTile label="OpenRouter" value={fmtUsd(totals.openrouter)} />
        <StatTile label="Anthropic" value={fmtUsd(totals.anthropic)} />
        <StatTile label="Combined" value={fmtUsd(totals.combined)} />
        <StatTile label="CVEs analyzed" value={(totals.cves_analyzed ?? 0).toLocaleString()} />
        <StatTile label="Cost / CVE" value={overallCost != null ? fmtUsd(overallCost, 4) : "—"}
                  color={overallCost != null ? "#3fb950" : undefined} />
      </div>

      <div className="panel">
        <div className="panel-header">Daily spend by provider</div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "2px solid var(--border)" }}>
                <th style={{ textAlign: "left", padding: 8 }}>Date</th>
                <th style={cellR}>OpenRouter</th>
                <th style={cellR}>Anthropic</th>
                <th style={cellR}>Total</th>
                <th style={cellR}>CVEs analyzed</th>
                <th style={cellR}>Cost / CVE</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const cpc = costPerCve(r.total, r.cves_analyzed);
                return (
                  <tr key={r.date} style={{ borderBottom: "1px solid var(--border)" }}>
                    <td style={{ padding: 8, fontWeight: 600 }}>{r.date}</td>
                    <td style={{ ...cellR, color: "var(--text-secondary)" }}>{fmtUsd(r.openrouter)}</td>
                    <td style={{ ...cellR, color: "var(--text-secondary)" }}>{fmtUsd(r.anthropic)}</td>
                    <td style={{ ...cellR, fontWeight: 600 }}>{fmtUsd(r.total)}</td>
                    <td style={cellR}>{r.cves_analyzed ? r.cves_analyzed.toLocaleString() : "—"}</td>
                    <td style={{ ...cellR, color: cpc != null ? "#3fb950" : "var(--text-muted)" }}>
                      {cpc != null ? fmtUsd(cpc, 4) : "—"}
                    </td>
                  </tr>
                );
              })}
              <tr style={{ borderTop: "2px solid var(--border)", fontWeight: 700 }}>
                <td style={{ padding: 8 }}>Total</td>
                <td style={cellR}>{fmtUsd(totals.openrouter)}</td>
                <td style={cellR}>{fmtUsd(totals.anthropic)}</td>
                <td style={cellR}>{fmtUsd(totals.combined)}</td>
                <td style={cellR}>{(totals.cves_analyzed ?? 0).toLocaleString()}</td>
                <td style={{ ...cellR, color: "#3fb950" }}>
                  {overallCost != null ? fmtUsd(overallCost, 4) : "—"}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {data.state?.last_poll && (
        <div style={{ marginTop: 8, fontSize: 11, color: "var(--text-muted)" }}>
          Last provider poll: {fmtTs(data.state.last_poll)}
          {data.state.error && <span style={{ color: "#f85149" }}> — {data.state.error}</span>}
        </div>
      )}
    </div>
  );
}

// ── Products ingestion ─────────────────────────────────────────────────

// Categorical slots 1 and 2, themed in App.css. `added` and `assessed` are
// two different events on the same row — a product added last week and
// assessed today lands in a different bucket in each — so they are grouped
// side by side, never stacked: neither series contains the other.
const SERIES = [
  { key: "added", name: "Products added", color: "var(--series-1)" },
  { key: "assessed", name: "Assessments written", color: "var(--series-2)" },
];

function LegendKey({ items }) {
  return (
    <div style={{ display: "flex", gap: 16, fontSize: 12, color: "var(--text-secondary)" }}>
      {items.map((sr) => (
        <span key={sr.key}>
          <span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 2,
                         background: sr.color, marginRight: 5, verticalAlign: "middle" }} />
          {sr.name}
        </span>
      ))}
    </div>
  );
}

// Label the extreme only. A number on every column is noise, and the tooltip
// plus the table below carry every other value.
function peakLabel(peak) {
  return function PeakLabel(props) {
    const { x, y, width, value } = props;
    if (!value || value !== peak) return null;
    return (
      <text x={x + width / 2} y={y - 5} textAnchor="middle"
            fill="var(--text-secondary)" fontSize={10} fontWeight={700}>
        {value.toLocaleString()}
      </text>
    );
  };
}

function IngestionTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "var(--bg-secondary)", border: "1px solid var(--border)",
                  borderRadius: 6, padding: "8px 10px", fontSize: 12 }}>
      <div style={{ fontWeight: 700, marginBottom: 4, color: "var(--text-primary)" }}>{label}</div>
      {SERIES.map((sr) => {
        const row = payload.find((pl) => pl.dataKey === sr.key);
        return (
          <div key={sr.key} style={{ color: "var(--text-secondary)" }}>
            <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2,
                           background: sr.color, marginRight: 6 }} />
            {sr.name}:{" "}
            <span style={{ color: "var(--text-primary)", fontWeight: 600 }}>
              {(row?.value ?? 0).toLocaleString()}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function ProductsTab({ data, loading, days }) {
  if (!data) return null;
  const buckets = data.buckets || [];
  const t = data.totals || {};
  const perDay = buckets.length ? t.added_in_window / buckets.length : 0;
  const assessedPct = t.products ? (t.assessed / t.products) * 100 : 0;
  const peakAdded = Math.max(0, ...buckets.map((b) => b.added));
  const peakAssessed = Math.max(0, ...buckets.map((b) => b.assessed));
  const nothingYet = t.added_in_window === 0 && t.assessed_in_window === 0;
  const cellR = { padding: 8, textAlign: "right" };

  return (
    <div style={{ opacity: loading ? 0.6 : 1 }}>
      <div style={{ display: "flex", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
        <StatTile label={`Added in ${days}d`} value={(t.added_in_window ?? 0).toLocaleString()}
                  color="var(--series-1)" />
        <StatTile label="Added / day (avg)" value={perDay.toFixed(perDay < 10 ? 1 : 0)} />
        <StatTile label={`Assessed in ${days}d`} value={(t.assessed_in_window ?? 0).toLocaleString()}
                  color="var(--series-2)" />
        <StatTile label="Products total" value={(t.products ?? 0).toLocaleString()} />
        <StatTile label="Assessed" value={(t.assessed ?? 0).toLocaleString()}
                  total={(t.products ?? 0).toLocaleString()} color={pctColor(assessedPct)}
                  suffix={t.products ? ` (${assessedPct.toFixed(0)}%)` : ""} />
        <StatTile label="Awaiting assessment" value={(t.unassessed ?? 0).toLocaleString()}
                  color={t.unassessed > 0 ? "#d29922" : "#3fb950"} />
      </div>

      <div className="panel" style={{ marginBottom: 12 }}>
        <div className="panel-header" style={{ display: "flex", alignItems: "center", gap: 12 }}>
          Daily product ingestion
          <span style={{ marginLeft: "auto" }}><LegendKey items={SERIES} /></span>
        </div>
        <div className="panel-body">
          {nothingYet ? (
            <div style={{ padding: 20, textAlign: "center", color: "var(--text-secondary)", fontSize: 13 }}>
              No products added or assessed in the last {days} day{days > 1 ? "s" : ""}.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={buckets} margin={{ top: 18, right: 12, left: 4, bottom: 40 }}
                        barGap={2}>
                {/* Horizontal hairlines only, solid — vertical rules add ink
                    without helping read a column's height. */}
                <CartesianGrid stroke="var(--border)" vertical={false} />
                <XAxis dataKey="date" tick={{ fill: "var(--text-secondary)", fontSize: 10 }}
                       angle={-45} textAnchor="end" height={54}
                       interval={days > 20 ? Math.floor(days / 15) : 0} />
                <YAxis tick={{ fill: "var(--text-secondary)", fontSize: 11 }}
                       allowDecimals={false}
                       tickFormatter={(v) => v.toLocaleString()} />
                <Tooltip content={<IngestionTooltip />}
                         cursor={{ fill: "var(--bg-hover)", opacity: 0.35 }} />
                <Legend wrapperStyle={{ fontSize: 12, color: "var(--text-secondary)" }} />
                {/* isAnimationActive off: the chart re-fetches on every
                    window change, and re-playing the grow-in each time reads
                    as a flash rather than as information. */}
                {SERIES.map((sr, i) => (
                  <Bar key={sr.key} dataKey={sr.key} name={sr.name} fill={sr.color}
                       maxBarSize={24} radius={[4, 4, 0, 0]}
                       isAnimationActive={false}>
                    <LabelList dataKey={sr.key}
                               content={peakLabel(i === 0 ? peakAdded : peakAssessed)} />
                  </Bar>
                ))}
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {t.undated > 0 && (
        <div className="panel" style={{ marginBottom: 12, padding: "10px 14px", fontSize: 12,
                                        color: "var(--text-secondary)" }}>
          <b style={{ color: "var(--text-primary)" }}>{t.undated.toLocaleString()}</b> product
          {t.undated === 1 ? "" : "s"} entered the table before arrival dates were
          recorded, so they are counted in the totals but appear on no day above.
          A bulk seed (<code>populate-products.py</code>) lands as a single spike on
          the day it ran.
        </div>
      )}

      {/* Table view: the chart's values are never color-gated. */}
      <div className="panel">
        <div className="panel-header">By day</div>
        <div style={{ overflowX: "auto", maxHeight: 320 }}>
          <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "2px solid var(--border)" }}>
                <th style={{ textAlign: "left", padding: 8 }}>Date</th>
                <th style={cellR}>Products added</th>
                <th style={cellR}>Assessments written</th>
              </tr>
            </thead>
            <tbody>
              {[...buckets].reverse().map((b) => (
                <tr key={b.date} style={{ borderBottom: "1px solid var(--border)" }}>
                  <td style={{ padding: 8, fontWeight: 600 }}>{b.date}</td>
                  <td style={{ ...cellR, color: b.added ? "var(--text-primary)" : "var(--text-muted)" }}>
                    {b.added ? b.added.toLocaleString() : "—"}
                  </td>
                  <td style={{ ...cellR, color: b.assessed ? "var(--text-primary)" : "var(--text-muted)" }}>
                    {b.assessed ? b.assessed.toLocaleString() : "—"}
                  </td>
                </tr>
              ))}
              <tr style={{ borderTop: "2px solid var(--border)", fontWeight: 700 }}>
                <td style={{ padding: 8 }}>Total</td>
                <td style={cellR}>{(t.added_in_window ?? 0).toLocaleString()}</td>
                <td style={cellR}>{(t.assessed_in_window ?? 0).toLocaleString()}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function _tabFromPath() {
  const m = window.location.pathname.match(/^\/health\/(\w+)/);
  return m && TABS.includes(m[1]) ? m[1] : "coverage";
}

export default function HealthPanel() {
  const [tab, setTabState] = useState(_tabFromPath);
  const [days, setDays] = useState(5);

  // Keep the URL in sync with the active tab (/health, /health/agreement)
  // and follow browser back/forward within the health view.
  const setTab = (t) => {
    const path = t === "coverage" ? "/health" : `/health/${t}`;
    if (window.location.pathname !== path) window.history.pushState(null, "", path);
    setTabState(t);
  };
  useEffect(() => {
    const onPop = () => {
      if (window.location.pathname.startsWith("/health")) setTabState(_tabFromPath());
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const [coverageData, setCoverageData] = useState(null);
  const [agreementData, setAgreementData] = useState(null);
  const [spendData, setSpendData] = useState(null);
  const [productsData, setProductsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadCoverage = useCallback((d) => {
    setLoading(true);
    setError(null);
    fetchScoreHealth(d)
      .then(setCoverageData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const loadAgreement = useCallback((d) => {
    setLoading(true);
    setError(null);
    fetchScoreAgreement(d)
      .then(setAgreementData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const loadSpend = useCallback((d) => {
    setLoading(true);
    setError(null);
    fetchSpendHistory(d)
      .then(setSpendData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const loadProducts = useCallback((d) => {
    setLoading(true);
    setError(null);
    fetchProductIngestion(d)
      .then(setProductsData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (tab === "coverage") loadCoverage(days);
    else if (tab === "agreement") loadAgreement(days);
    else if (tab === "products") loadProducts(days);
    else loadSpend(days);
  }, [days, tab, loadCoverage, loadAgreement, loadSpend, loadProducts]);

  const descriptions = {
    coverage: `CVEs published (or imported) in the last ${days} day${days > 1 ? "s" : ""} that are missing scores.`,
    agreement: `CVSS v3.1 agreement between official (NVD/Vendor) and Vinnie's (gemma) scores for the last ${days} day${days > 1 ? "s" : ""}.`,
    spend: `Daily LLM provider spend and cost per CVE analyzed for the last ${days} day${days > 1 ? "s" : ""} (ET days, polled hourly by vinnie-control).`,
    products: `New products entering the prevalence master list, and assessments written, per day for the last ${days} day${days > 1 ? "s" : ""}. Products arrive from CVE affected[] data via vinnie-prevalence.`,
  };

  return (
    <div>
      <div className="panel" style={{ marginBottom: 12 }}>
        <div className="panel-header" style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ display: "flex", gap: 2 }}>
            {TABS.map((t) => (
              <button key={t} className={`table-tab ${tab === t ? "active" : ""}`}
                      onClick={() => setTab(t)} style={{ textTransform: "capitalize" }}>
                {t}
              </button>
            ))}
          </div>
          <div style={{ marginLeft: "auto", display: "flex", gap: 6, alignItems: "center" }}>
            <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>Window:</span>
            {DAY_OPTIONS.map((d) => (
              <button key={d} className={`table-tab ${days === d ? "active" : ""}`}
                      onClick={() => setDays(d)}>
                {d}d
              </button>
            ))}
          </div>
        </div>
        <p style={{ fontSize: 12, color: "var(--text-secondary)", margin: "6px 0 0" }}>
          {descriptions[tab]}
        </p>
      </div>

      {error && <div className="error">API error: {error}</div>}
      {loading && !coverageData && !agreementData && !spendData && !productsData && (
        <div className="loading">Loading...</div>
      )}

      {tab === "coverage" && coverageData && (
        <CoverageTab days={days} data={coverageData} loading={loading} />
      )}
      {tab === "agreement" && agreementData && (
        <AgreementTab data={agreementData} loading={loading} days={days} />
      )}
      {tab === "spend" && spendData && (
        <SpendTab data={spendData} loading={loading} />
      )}
      {tab === "products" && productsData && (
        <ProductsTab data={productsData} loading={loading} days={days} />
      )}
    </div>
  );
}
